import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Mapir from "mapir-react-component";
import { mapirMap } from "../utils/mapirMap";
import "mapir-react-component/dist/index.css";
import "mapbox-gl/dist/mapbox-gl.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MAP_REQUEST_DEBOUNCE_MS = 300;
const MAP_POPUP_SAFE_HEIGHT = 340;
const MAP_POPUP_SAFE_GUTTER = 18;
type MarkerCategory = "attraction" | "restaurant" | "cafe" | "accommodation" | "default";
interface MapCategoryFilter {
  id: string;
  name: string;
  slug: string;
}

const FALLBACK_CATEGORY_FILTERS: MapCategoryFilter[] = [
  { id: "all", name: "همه مکان‌ها", slug: "" },
  { id: "attractions", name: "دیدنی‌ها", slug: "attractions" },
  { id: "restaurants", name: "رستوران‌ها", slug: "restaurants" },
  { id: "cafes", name: "کافه‌ها", slug: "cafes" },
  { id: "accommodations", name: "اقامتگاه‌ها", slug: "accommodations" },
];

interface MapBounds {
  getWest: () => number;
  getSouth: () => number;
  getEast: () => number;
  getNorth: () => number;
}

interface MapInstance {
  getBounds: () => MapBounds;
  getZoom: () => number;
  fitBounds: (bounds: [[number, number], [number, number]], options?: Record<string, unknown>) => void;
  project?: (coordinates: [number, number]) => { x: number; y: number };
  getContainer?: () => HTMLElement;
  panBy?: (offset: [number, number], options?: Record<string, unknown>) => void;
}

interface PlaceCategory {
  id?: number | string | null;
  name?: string | null;
  slug?: string | null;
  marker_type?: MarkerCategory | null;
}

interface MapFilterCategory {
  id: number | string;
  name: string;
  slug: string;
  marker_type: MarkerCategory;
}

interface MapResponse {
  data?: {
    features?: unknown;
    meta?: {
      categories?: unknown;
    };
  };
}

interface PlaceFeature {
  type: "place";
  place_id: string;
  name: string;
  latitude: number;
  longitude: number;
  thumbnail_url?: string | null;
  description?: string | null;
  rating?: number | null;
  review_count?: number | null;
  category?: PlaceCategory | null;
}

interface ClusterFeature {
  type: "cluster";
  cluster_id: string;
  latitude: number;
  longitude: number;
  count: number;
  expansion_zoom?: number;
  bounds: { west: number; south: number; east: number; north: number };
}

type MapFeature = PlaceFeature | ClusterFeature;

interface CityPlacesMapProps {
  cityId: number;
  cityName: string;
  latitude: number;
  longitude: number;
}

const toDataUrl = (svg: string) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

const isMarkerCategory = (value: unknown): value is MarkerCategory => (
  value === "attraction"
  || value === "restaurant"
  || value === "cafe"
  || value === "accommodation"
  || value === "default"
);

const getMarkerCategory = (category?: PlaceCategory | null): MarkerCategory => (
  isMarkerCategory(category?.marker_type) ? category.marker_type : "default"
);

const getMapFilterCategories = (value: unknown): MapFilterCategory[] => {
  if (!Array.isArray(value)) return [];

  const validCategories = value.filter((category): category is MapFilterCategory => (
    typeof category?.id === "string" || typeof category?.id === "number"
  ) && typeof category?.name === "string"
    && typeof category?.slug === "string"
    && isMarkerCategory(category?.marker_type));

  return validCategories.filter((category, index) => (
    validCategories.findIndex(({ slug }) => slug === category.slug) === index
  ));
};

const deduplicateFeatures = (features: MapFeature[]): MapFeature[] => {
  const seen = new Set<string>();

  return features.filter((feature) => {
    const key = feature.type === "place"
      ? `place:${feature.place_id}`
      : `cluster:${feature.cluster_id}`;

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const markerIcon = (category: MarkerCategory, color: string) => {
  switch (category) {
    case "attraction":
      return `<path d="m19 11 2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5-3.6-3.5 5-.7Z" fill="${color}"/>`;
    case "restaurant":
      return `<path d="M13 12v6m3-6v6m-3-3h3m-1.5 3v8M23 12v14m0-14c2 2 2 6 0 8" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
    case "cafe":
      return `<path d="M12.5 14h12v7a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4Zm12 3.5h1a3 3 0 0 1 0 6h-1M14 11.5h7" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
    case "accommodation":
      return `<path d="M12 21v5m14-5v5M12 22h14M13 18h12v4H13Zm2-3h3a2 2 0 0 1 2 2v1h-7v-1a2 2 0 0 1 2-2Zm6 0h2a2 2 0 0 1 2 2v1h-4Z" fill="${color}"/>`;
    default:
      return `<circle cx="19" cy="19" r="5" fill="${color}"/>`;
  }
};

const markerColors: Record<MarkerCategory, { fill: string; icon: string }> = {
  attraction: { fill: "#bcebd2", icon: "#075c37" },
  restaurant: { fill: "#f9d978", icon: "#4a3600" },
  cafe: { fill: "#f0c58a", icon: "#5f3a0c" },
  accommodation: { fill: "#b9e1ef", icon: "#0b5b75" },
  default: { fill: "#ffffff", icon: "#038545" },
};

const createPlaceMarkerImage = (category: MarkerCategory, selected = false) => {
  const colors = markerColors[category];
  const fill = selected ? "#171717" : colors.fill;
  const icon = selected ? "#ffffff" : colors.icon;

  return toDataUrl(`
  <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
    <defs>
      <filter id="marker-shadow" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2.2" flood-color="#1f2937" flood-opacity=".28"/>
      </filter>
    </defs>
    <circle cx="26" cy="26" r="20" fill="${fill}" filter="url(#marker-shadow)"/>
    <g transform="translate(7 7)">${markerIcon(category, icon)}</g>
  </svg>
`);
};

const placeMarkerImages: Record<MarkerCategory, string> = {
  attraction: createPlaceMarkerImage("attraction"),
  restaurant: createPlaceMarkerImage("restaurant"),
  cafe: createPlaceMarkerImage("cafe"),
  accommodation: createPlaceMarkerImage("accommodation"),
  default: createPlaceMarkerImage("default"),
};

const selectedPlaceMarkerImages: Record<MarkerCategory, string> = {
  attraction: createPlaceMarkerImage("attraction", true),
  restaurant: createPlaceMarkerImage("restaurant", true),
  cafe: createPlaceMarkerImage("cafe", true),
  accommodation: createPlaceMarkerImage("accommodation", true),
  default: createPlaceMarkerImage("default", true),
};

const getClusterMarkerImage = (count: number) => toDataUrl(`
  <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
    <defs>
      <filter id="cluster-shadow" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2.2" flood-color="#1f2937" flood-opacity=".25"/>
      </filter>
    </defs>
    <circle cx="28" cy="28" r="23" fill="#ffffff" filter="url(#cluster-shadow)"/>
    <text x="28" y="34" text-anchor="middle" font-family="Arial, sans-serif" font-size="17" font-weight="700" fill="#171717">${count > 99 ? "99+" : count}</text>
  </svg>
`);

const formatNumber = (value: number, maximumFractionDigits = 0) => new Intl.NumberFormat("fa-IR", {
  maximumFractionDigits,
}).format(value);

const CityPlacesMap: React.FC<CityPlacesMapProps> = ({ cityId, cityName, latitude, longitude }) => {
  // Mapir compares these props by reference. Keeping them stable prevents a
  // data-loading re-render from resetting the user's current zoom to 12.
  const initialCenter = useMemo<[number, number]>(() => [longitude, latitude], [longitude, latitude]);
  const initialZoom = useMemo<[number]>(() => [12], []);
  const mapRef = useRef<MapInstance | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const hasInitialRequestRef = useRef(false);
  const hasViewportInteractionRef = useRef(false);
  const requestedViewportKeyRef = useRef<string | null>(null);
  const currentMapScopeRef = useRef({ cityId, category: "" });
  const [features, setFeatures] = useState<MapFeature[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<PlaceFeature | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryFilters, setCategoryFilters] = useState<MapCategoryFilter[]>(FALLBACK_CATEGORY_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  currentMapScopeRef.current = { cityId, category: selectedCategory };

  const loadVisiblePlaces = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;

    const bounds = map.getBounds();
    const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
      .map((coordinate) => coordinate.toFixed(5));
    const zoom = Math.round(map.getZoom());
    const viewportKey = `${cityId}:${selectedCategory || "all"}:${bbox.join(",")}:${zoom}`;

    // Map.ir can emit repeated move-end events while it initializes or resizes.
    // The same viewport must never trigger another API request.
    if (requestedViewportKeyRef.current === viewportKey) return;
    requestedViewportKeyRef.current = viewportKey;

    const params = new URLSearchParams({
      bbox: bbox.join(","),
      zoom: String(zoom),
      limit: "200",
      city_id: String(cityId),
    });
    if (selectedCategory) params.set("category", selectedCategory);

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await fetch(`${BASE_URL}/api/map/places/?${params.toString()}`, {
        signal: controller.signal,
      });
      // A cache revalidation can legitimately return 304 without a JSON body.
      // The last successful markers remain visible in that case.
      if (response.status === 304) return;
      if (!response.ok) throw new Error("دریافت مکان‌های نقشه ناموفق بود.");

      const body = await response.json() as MapResponse;
      const mapFeatures = body?.data?.features;
      if (!Array.isArray(mapFeatures)) throw new Error("پاسخ نقشه معتبر نیست.");

      // A response that belonged to the previous city or category must never
      // replace the markers after the user has switched filters.
      const currentScope = currentMapScopeRef.current;
      if (currentScope.cityId !== cityId || currentScope.category !== selectedCategory) return;

      const responseCategories = getMapFilterCategories(body?.data?.meta?.categories);
      if (responseCategories.length > 0) {
        setCategoryFilters([
          FALLBACK_CATEGORY_FILTERS[0],
          ...responseCategories.map(({ id, name, slug }) => ({ id: String(id), name, slug })),
        ]);
      }

      setFeatures(deduplicateFeatures(
        mapFeatures.filter((feature: MapFeature) =>
          Number.isFinite(feature.latitude)
          && Number.isFinite(feature.longitude)
          && (
            feature.type !== "place"
            || !selectedCategory
            || feature.category?.slug === selectedCategory
          ),
        ),
      ));
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        setErrorMessage("دریافت مکان‌های این محدوده ممکن نشد.");
      }
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, [cityId, selectedCategory]);

  const scheduleViewportRequest = useCallback((immediate = false) => {
    if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current);
    if (immediate) {
      void loadVisiblePlaces();
      return;
    }

    debounceTimerRef.current = window.setTimeout(() => {
      void loadVisiblePlaces();
    }, MAP_REQUEST_DEBOUNCE_MS);
  }, [loadVisiblePlaces]);

  useEffect(() => {
    hasInitialRequestRef.current = false;
    hasViewportInteractionRef.current = false;
    requestedViewportKeyRef.current = null;
    setSelectedPlace(null);

    if (mapRef.current) {
      hasInitialRequestRef.current = true;
      scheduleViewportRequest(true);
    }
  }, [cityId, latitude, longitude, selectedCategory, scheduleViewportRequest]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
    if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current);
  }, []);

  const handleMapReady = (map?: MapInstance) => {
    if (!map) return;
    mapRef.current = map;
    if (!hasInitialRequestRef.current) {
      hasInitialRequestRef.current = true;
      scheduleViewportRequest(true);
    }
  };

  const handleClusterClick = (cluster: ClusterFeature) => {
    setSelectedPlace(null);
    hasViewportInteractionRef.current = true;
    mapRef.current?.fitBounds(
      [
        [cluster.bounds.west, cluster.bounds.south],
        [cluster.bounds.east, cluster.bounds.north],
      ],
      {
        padding: 48,
        maxZoom: cluster.expansion_zoom,
        duration: 450,
      },
    );
  };

  const focusSelectedPlace = useCallback((place: PlaceFeature) => {
    setSelectedPlace(place);

    // Leave enough room around the selected marker for the popup card. Mapbox
    // recalculates the popup anchor after this short camera movement, so it can
    // still choose the side with the most available space.
    window.requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map?.project || !map.getContainer || !map.panBy) return;

      const container = map.getContainer();
      const { width, height } = container.getBoundingClientRect();
      if (!width || !height) return;

      const markerPoint = map.project([place.longitude, place.latitude]);
      const targetX = width / 2;
      const targetY = Math.min(
        height - MAP_POPUP_SAFE_GUTTER,
        Math.max(height * 0.72, MAP_POPUP_SAFE_HEIGHT + MAP_POPUP_SAFE_GUTTER),
      );

      // panBy moves the camera in the supplied direction, which shifts the
      // projected marker in the opposite direction on screen.
      const offset: [number, number] = [
        markerPoint.x - targetX,
        markerPoint.y - targetY,
      ];

      if (Math.abs(offset[0]) < 8 && Math.abs(offset[1]) < 8) return;
      map.panBy(offset, { duration: 280, essential: true });
    });
  }, []);

  const handleCategoryChange = (categorySlug: string) => {
    if (categorySlug === selectedCategory) return;

    // Abort immediately rather than leaving a prior category response visible
    // while React schedules the next viewport request.
    abortControllerRef.current?.abort();
    requestedViewportKeyRef.current = null;
    setSelectedCategory(categorySlug);
  };

  return (
    <section className="site-container py-8 tablet:py-10" aria-labelledby="city-places-map-title">
      <div className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            {/* <h2 id="city-places-map-title" className="text-lg font-myYekanFaNumExtraBold text-[#222222] tablet:text-xl desktop:text-2xl">
              مکان‌ها روی نقشه
            </h2> */}
          </div>
          {isLoading && <span className="shrink-0 text-[11px] text-gray-500 font-myYekanFaNumRegular">در حال به‌روزرسانی</span>}
        </div>
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" role="group" aria-label="فیلتر دسته‌بندی مکان‌ها">
          {categoryFilters.map((filter) => {
            const isSelected = selectedCategory === filter.slug;
            return (
              <button
                key={filter.id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => handleCategoryChange(filter.slug)}
                className={`h-9 shrink-0 rounded-xl border px-3 text-xs font-myYekanFaNumMedium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] focus-visible:ring-offset-2 ${
                  isSelected
                    ? "border-[#038545] bg-[#038545] text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:border-[#97d2b5] hover:bg-[#f3fefa]"
                }`}
              >
                {filter.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-[#f3fefa]">
        <Mapir
          Map={mapirMap}
          center={initialCenter}
          zoom={initialZoom}
          interactive
          hash={false}
          trackResize
          onClick={() => setSelectedPlace(null)}
          containerStyle={{ width: "100%", height: "clamp(360px, 58vw, 460px)" }}
          onStyleLoad={handleMapReady}
          onDragStart={() => { hasViewportInteractionRef.current = true; }}
          onZoomStart={() => { hasViewportInteractionRef.current = true; }}
          onMoveEnd={() => {
            if (!hasViewportInteractionRef.current) return;
            hasViewportInteractionRef.current = false;
            scheduleViewportRequest();
          }}
        >
          {features.map((feature) => {
            if (feature.type === "cluster") {
              return (
                <Mapir.Marker
                  key={feature.cluster_id}
                  coordinates={[feature.longitude, feature.latitude]}
                  anchor="center"
                  Image={getClusterMarkerImage(feature.count)}
                  style={{ width: "56px", cursor: "pointer" }}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                    handleClusterClick(feature);
                  }}
                  aria-label={`${feature.count} مکان؛ برای بزرگ‌نمایی انتخاب کنید`}
                />
              );
            }

            const markerCategory = getMarkerCategory(feature.category);
            const isSelected = selectedPlace?.place_id === feature.place_id;

            return (
              <Mapir.Marker
                key={feature.place_id}
                coordinates={[feature.longitude, feature.latitude]}
                anchor="center"
                Image={isSelected ? selectedPlaceMarkerImages[markerCategory] : placeMarkerImages[markerCategory]}
                style={{ width: isSelected ? "52px" : "48px", cursor: "pointer" }}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  focusSelectedPlace(feature);
                }}
                onMouseEnter={() => setSelectedPlace(feature)}
                aria-label={feature.category?.name ? `${feature.category.name}: ${feature.name}` : feature.name}
              />
            );
          })}

          {selectedPlace && (
            <Mapir.Popup
              coordinates={[selectedPlace.longitude, selectedPlace.latitude]}
              offset={18}
              className="lootka-city-map-popup"
            >
              <Link to={`/places/${selectedPlace.place_id}`} className="block overflow-hidden bg-white text-right font-myYekanFaNumRegular focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]">
                {selectedPlace.thumbnail_url ? (
                  <img src={selectedPlace.thumbnail_url} alt="" className="h-48 w-full object-cover" />
                ) : (
                  <div className="flex h-48 items-center justify-center bg-[#f3fefa] text-xs text-gray-500">تصویر این مکان موجود نیست</div>
                )}
                <div className="p-3">
                  <p className="line-clamp-1 text-sm leading-6 font-myYekanFaNumDemiBold text-[#222222]">{selectedPlace.name}</p>
                  <div className="mt-2 flex items-center justify-between gap-3 text-xs text-gray-500">
                  <span>{selectedPlace.category?.name || "مکان دیدنی"}</span>
                  {selectedPlace.rating != null && <span>{selectedPlace.rating.toFixed(1)} از ۵</span>}
                  </div>
                  {selectedPlace.description?.trim() && (
                    <p className="mt-2 line-clamp-3 break-words text-justify text-[11px] leading-5 text-gray-600 font-myYekanFaNumRegular">
                      {selectedPlace.description}
                    </p>
                  )}
                </div>
              </Link>
            </Mapir.Popup>
          )}
        </Mapir>

        {!isLoading && !errorMessage && features.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/70 p-4 text-center text-sm text-gray-600 font-myYekanFaNumRegular">
            در این محدوده مکانی برای نمایش نیست.
          </div>
        )}
        {errorMessage && (
          <div className="pointer-events-none absolute inset-x-4 bottom-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-center text-xs text-red-700 font-myYekanFaNumRegular">
            {errorMessage}
          </div>
        )}
      </div>
    </section>
  );
};

export default CityPlacesMap;
